import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';

import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ChartModule } from 'primeng/chart';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { MeterGroupModule } from 'primeng/metergroup';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { DatePickerModule } from 'primeng/datepicker';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { DividerModule } from 'primeng/divider';
import { configProviders } from './config';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { SliderModule } from 'primeng/slider';
import { ProgressBar, ProgressBarModule } from 'primeng/progressbar';
import { DialogModule } from 'primeng/dialog';
import { apiProviders } from './api';
import { sessionProviders } from './session';
import { storageDbProviders } from './storage-db';
import { MessageService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import {
  simplifiedTableDeclarations,
  simplifiedTableExports,
} from './simplified-table';
import { DrawerModule } from 'primeng/drawer';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { CardModule } from 'primeng/card';
import { uiWidgetsDeclarations, uiWidgetsExports } from './ui-widgets';
import { FormlyModule } from '@ngx-formly/core';
import {
  FormCategoryWrapperType,
  FormCheckboxFieldType,
  FormFieldsetWrapperType,
  FormFileFieldType,
  FormFormeditorFieldType,
  FormInputFieldType,
  FormPasswordFieldType,
  FormRadioFieldType,
  FormRootWrapperType,
  FormRowWrapperType,
  formsDeclarations,
  FormSelectFieldType,
  formsExports,
  FormTextareaFieldType,
  FormToggleFieldType,
} from './forms';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PasswordModule } from 'primeng/password';
import { FieldsetModule } from 'primeng/fieldset';
import { SelectModule } from 'primeng/select';
import { FormDateFieldType } from './forms/components/field-types/form-date-field';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FileUploadModule } from 'primeng/fileupload';
import { FormFieldsArrayType } from './forms/components/array-types/form-fields-array';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { PopoverModule } from 'primeng/popover';
import { ToastModule } from 'primeng/toast';
import { InputGroup, InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ListboxModule } from 'primeng/listbox';
import { impliancyDeclarations, impliancyExports } from './impliancy';

@NgModule({
  declarations: [
    ...simplifiedTableDeclarations,
    ...uiWidgetsDeclarations,
    ...formsDeclarations,
    ...simplifiedTableDeclarations,
    ...impliancyDeclarations,
  ],
  exports: [
    ...simplifiedTableExports,
    ...uiWidgetsExports,
    ...impliancyExports,

    TranslateModule,
    FormlyModule,

    BreadcrumbModule,
    ChartModule,
    SelectButtonModule,
    FormsModule,
    AvatarModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    TableModule,
    MeterGroupModule,
    InputTextModule,
    InputNumberModule,
    MenuModule,
    TagModule,
    MeterGroupModule,
    OverlayBadgeModule,
    DatePickerModule,
    MenuModule,
    BadgeModule,
    RippleModule,
    AvatarModule,
    TooltipModule,
    DividerModule,
    MultiSelectModule,
    DropdownModule,
    SliderModule,
    ProgressBarModule,
    DialogModule,
    MessageModule,
    DrawerModule,
    ToggleSwitchModule,
    AvatarGroupModule,
    CardModule,
    IftaLabelModule,
    PasswordModule,
    FieldsetModule,
    SelectModule,
    DatePickerModule,
    RadioButtonModule,
    FileUploadModule,
    TextareaModule,
    CheckboxModule,
    PopoverModule,
    InputGroupModule,
    InputGroupAddonModule,
    ListboxModule,
    ToastModule,

    ...formsExports,
  ],
  imports: [
    CommonModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    FormlyModule.forRoot({
      extras: {
        resetFieldOnHide: false,
      },
      types: [
        { name: 'input', component: FormInputFieldType },
        { name: 'password', component: FormPasswordFieldType },
        { name: 'select', component: FormSelectFieldType },
        { name: 'date', component: FormDateFieldType },
        { name: 'radio', component: FormRadioFieldType },
        { name: 'file', component: FormFileFieldType },
        { name: 'formeditor', component: FormFormeditorFieldType },
        { name: 'toggle', component: FormToggleFieldType },
        { name: 'textarea', component: FormTextareaFieldType },
        { name: 'checkbox', component: FormCheckboxFieldType },

        { name: 'fields-array', component: FormFieldsArrayType },
      ],
      wrappers: [
        { name: 'fieldset', component: FormFieldsetWrapperType },
        { name: 'category', component: FormCategoryWrapperType },
        { name: 'root', component: FormRootWrapperType },
        { name: 'row', component: FormRowWrapperType },
      ],
    }),

    BreadcrumbModule,
    ChartModule,
    SelectButtonModule,
    AvatarModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    TableModule,
    MeterGroupModule,
    InputTextModule,
    InputNumberModule,
    MenuModule,
    TagModule,
    MeterGroupModule,
    OverlayBadgeModule,
    DatePickerModule,
    MenuModule,
    BadgeModule,
    RippleModule,
    AvatarModule,
    TooltipModule,
    DividerModule,
    MultiSelectModule,
    DropdownModule,
    SliderModule,
    ProgressBarModule,
    DialogModule,
    MessageModule,
    DrawerModule,
    ToggleSwitchModule,
    AvatarGroupModule,
    CardModule,
    IftaLabelModule,
    PasswordModule,
    FieldsetModule,
    SelectModule,
    DatePickerModule,
    RadioButtonModule,
    FileUploadModule,
    TextareaModule,
    CheckboxModule,
    PopoverModule,
    InputGroupModule,
    InputGroupAddonModule,
    ListboxModule,
    ToastModule,

    //NgMultiSelectDropDownModule.forRoot(),
  ],
  providers: [TranslateService, MessageService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CommonSharedModule {
  static forRoot() {
    return {
      ngModule: CommonSharedModule,
      providers: [
        ...configProviders,
        ...storageDbProviders,
        ...apiProviders,
        ...sessionProviders,
      ],
    };
  }
}
